import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NavigationBadge, useNavigationBadgesStore } from "../..";
import { addStudy, fetchStudies } from "../../api/studyApi";
import { useStudyReportSummary } from "../../hooks/studyHooks";
import { StudyReportSummary } from "../../types/Rows";
import ItemsList from "../../components/lists/ItemsList";
import AddItemModal from "../template/addItemModal";
import { APP_CONFIG } from "../../config/appConfig";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import MagnifyingGlass from "../../assets/icons/MagnifyingGlass";
import { Study } from "../../types/Rows";
import { cn } from "../../lib/utils";
import { SPECIALTY_KEYS } from "../../components/lists/StoryView";
import { Skeleton } from "../../components/ui/Skeleton";

const SPECIALTIES = [
  { key: "hematologia",  label: "Hematologia",   dot: "#5B8FD8" },
  { key: "kardiologia",  label: "Kardiologia",    dot: "#2BAE72" },
  { key: "pulmonologia", label: "Pulmonologia",   dot: "#F4A832" },
  { key: "neurologia",   label: "Neurologia",     dot: "#9060D0" },
  { key: "onkologia",    label: "Onkologia",      dot: "#E05050" },
];

type StatusFilterKey = "toVerify" | "toCorrect" | "accepted" | "inProgress";

const STATUS_FILTERS: { key: StatusFilterKey; label: string; dot: string; activeBg: string; activeBadge: string }[] = [
  { key: "toVerify",   label: "Do weryfikacji", dot: "#C07810", activeBg: "bg-[#FEF3E215]", activeBadge: "bg-[#FEF3E2] text-[#C07810]" },
  { key: "toCorrect",  label: "Do korekty",     dot: "#C03030", activeBg: "bg-[#FEE8E815]", activeBadge: "bg-[#FEE8E8] text-[#C03030]" },
  { key: "accepted",   label: "Zaakceptowane",  dot: "#1A8055", activeBg: "bg-[#E6F7F115]", activeBadge: "bg-[#E6F7F1] text-[#1A8055]" },
  { key: "inProgress", label: "W trakcie",      dot: "#999",    activeBg: "bg-[#F0F0F015]", activeBadge: "bg-[#F0F0F0] text-[#555]"    },
];

export const Root = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const setItems = useNavigationBadgesStore((state: any) => state.setItems);

  // Skeleton tylko przy pierwszym wczytaniu; odświeżanie w tle zostawia listę (wcześniej isFetching: lista migała)
  const { data = [], isFetched } = useQuery(["Studies"], fetchStudies, { initialData: [] });
  const isFirstLoad = !isFetched;
  const { data: summaries = [] } = useStudyReportSummary();
  const summaryMap = useMemo(
    () => Object.fromEntries((summaries as StudyReportSummary[]).map((s) => [s.studyId, s])),
    [summaries]
  );

  useEffect(() => {
    if (data) setItems([{ text: "Badania", url: "/" } as NavigationBadge]);
  }, [data, setItems]);

  const { mutate: add } = useMutation(
    async (name: string) => addStudy(name),
    { onSuccess: () => queryClient.invalidateQueries(["Studies"]) }
  );

  const [activeSpecialty, setActiveSpecialty] = useState<string | null>(null);
  const [activeStatus, setActiveStatus] = useState<StatusFilterKey | null>(null);
  const [search, setSearch] = useState("");

  const specialtyCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    SPECIALTIES.forEach(({ key }) => {
      counts[key] = (data as Study[]).filter((s) =>
        s.description?.toLowerCase().includes(key)
      ).length;
    });
    return counts;
  }, [data]);

  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilterKey, number> = { toVerify: 0, toCorrect: 0, accepted: 0, inProgress: 0 };
    (data as Study[]).forEach((s) => {
      const sum = (summaryMap as Record<string, StudyReportSummary>)[s.id];
      if (!sum) return;
      STATUS_FILTERS.forEach(({ key }) => {
        if (sum[key] > 0) counts[key]++;
      });
    });
    return counts;
  }, [data, summaryMap]);

  const filtered = useMemo(() => {
    let result = data as Study[];
    if (activeSpecialty) {
      result = result.filter((s) =>
        s.description?.toLowerCase().includes(activeSpecialty)
      );
    }
    if (activeStatus) {
      result = result.filter((s) => {
        const sum = (summaryMap as Record<string, StudyReportSummary>)[s.id];
        return sum && sum[activeStatus] > 0;
      });
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((s) => s.name.toLowerCase().includes(q));
    }
    return result;
  }, [data, activeSpecialty, activeStatus, search, summaryMap]);

  return (
    <div className="flex h-[calc(100vh-56px)] bg-[var(--sc-bg)]">
      {/* Sidebar */}
      <aside className="w-[220px] flex-shrink-0 bg-[var(--sc-surface)] border-r border-[var(--sc-border)] flex flex-col py-7 overflow-y-auto">
        {/* Search */}
        <div className="px-5 mb-6">
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--sc-muted)] pointer-events-none">
              <MagnifyingGlass />
            </span>
            <Input
              type="search"
              placeholder="Szukaj..."
              className="pl-8 w-full text-[13px]"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="px-5 mb-7">
          <p className="text-[10px] font-medium tracking-[0.1em] uppercase text-[var(--sc-muted)] mb-2.5">Widok</p>
          <button
            onClick={() => { setActiveSpecialty(null); setActiveStatus(null); }}
            className={cn(
              "flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-[13px] transition-colors text-left",
              activeSpecialty === null && activeStatus === null
                ? "bg-[#F4A83215] text-[var(--sc-text)] font-medium"
                : "text-[var(--sc-muted)] hover:bg-[var(--sc-bg)]"
            )}
          >
            <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", activeSpecialty === null && activeStatus === null ? "bg-[var(--sc-amber)]" : "bg-[#D0D0CE]")} />
            Wszystkie badania
            <span className={cn(
              "ml-auto text-[11px] px-1.5 py-0.5 rounded-full",
              activeSpecialty === null && activeStatus === null ? "bg-[#F4A83220] text-[#C07A10]" : "bg-[var(--sc-bg)] text-[var(--sc-muted)]"
            )}>
              {(data as Study[]).length}
            </span>
          </button>
        </div>

        <div className="px-5 mb-7">
          <p className="text-[10px] font-medium tracking-[0.1em] uppercase text-[var(--sc-muted)] mb-2.5">Status raportów</p>
          {STATUS_FILTERS.map(({ key, label, dot, activeBg, activeBadge }) => (
            <button
              key={key}
              onClick={() => { setActiveStatus(activeStatus === key ? null : key); setActiveSpecialty(null); }}
              className={cn(
                "flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-[13px] transition-colors text-left mb-0.5",
                activeStatus === key
                  ? cn(activeBg, "text-[var(--sc-text)] font-medium")
                  : "text-[var(--sc-muted)] hover:bg-[var(--sc-bg)]"
              )}
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: dot }} />
              {label}
              <span className={cn(
                "ml-auto text-[11px] px-1.5 py-0.5 rounded-full",
                activeStatus === key ? activeBadge : "bg-[var(--sc-bg)] text-[var(--sc-muted)]"
              )}>
                {statusCounts[key]}
              </span>
            </button>
          ))}
        </div>

        <div className="px-5">
          <p className="text-[10px] font-medium tracking-[0.1em] uppercase text-[var(--sc-muted)] mb-2.5">Specjalizacja</p>
          {SPECIALTIES.map(({ key, label, dot }) => (
            <button
              key={key}
              onClick={() => { setActiveSpecialty(activeSpecialty === key ? null : key); setActiveStatus(null); }}
              className={cn(
                "flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-[13px] transition-colors text-left mb-0.5",
                activeSpecialty === key
                  ? "bg-[#F4A83215] text-[var(--sc-text)] font-medium"
                  : "text-[var(--sc-muted)] hover:bg-[var(--sc-bg)]"
              )}
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: dot }} />
              {label}
              <span className={cn(
                "ml-auto text-[11px] px-1.5 py-0.5 rounded-full",
                activeSpecialty === key ? "bg-[#F4A83220] text-[#C07A10]" : "bg-[var(--sc-bg)] text-[var(--sc-muted)]"
              )}>
                {specialtyCounts[key] ?? 0}
              </span>
            </button>
          ))}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto px-12 py-9">
        {/* Header */}
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-[28px] font-light tracking-tight text-[var(--sc-text)] leading-none">
              {t("researchPlural")}
            </h1>
            {isFirstLoad ? (
              <Skeleton className="h-3.5 w-32 mt-1.5" />
            ) : (
            <p className="text-[13px] text-[var(--sc-muted)] mt-1">
              {filtered.length} {filtered.length === 1 ? "badanie" : "badań"}
              {activeSpecialty || activeStatus || search
                ? ` · ${[
                    activeSpecialty && SPECIALTIES.find((s) => s.key === activeSpecialty)?.label,
                    activeStatus && STATUS_FILTERS.find((s) => s.key === activeStatus)?.label,
                    search && `"${search}"`,
                  ].filter(Boolean).join(", ")}`
                : " klinicznych"}
            </p>
            )}
          </div>
          <AddItemModal saveItem={(name) => add(name)} text="Dodaj badanie" variant="default">
            <><span className="text-lg font-light mr-1">+</span> Dodaj badanie</>
          </AddItemModal>
        </div>

        {/* List */}
        <ItemsList
          isLoading={isFirstLoad}
          data={filtered}
          urlPrefix="study/"
          summaryMap={summaryMap}
        >
          {""}
        </ItemsList>
      </main>

      {/* Version label */}
      <div className="fixed bottom-2 right-3 text-[10px] text-[var(--sc-muted)] opacity-70 pointer-events-none select-none">
        v{APP_CONFIG.version}
      </div>
    </div>
  );
};

export default Root;


